import { Injectable, inject } from '@angular/core'

import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { uniqBy as _uniqBy } from 'lodash/fp'

import { EMPTY, Observable, concatMap, map, of, switchMap, take, tap } from 'rxjs'

import { ConversationThreadContextDataItemType } from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { ActionHandler } from '@flowx/designer/modules/node-actions/components/action-edit/action-edit.dictionary'
import { EventHandlerApiService } from '@flowx/designer/shared/api/event-handler.api'
import { NodeApiService } from '@flowx/designer/shared/api/node.api'
import { ReusableTemplatesApiService } from '@flowx/designer/shared/api/reusable-templates.api'
import { TemplateConfigApiService } from '@flowx/designer/shared/api/template-config.api'
import { createAction } from '@flowx/designer/shared/entities/action.entity'
import { createNodeActionSuccess } from '@flowx/designer/shared/store/actions/node-effects.actions'
import { TemplateConfigActions } from '@flowx/designer/shared/store/actions/template-config.actions'
import { selectedNodeActions } from '@flowx/designer/shared/store/selectors/node-actions.selectors'
import { nodesSelector } from '@flowx/designer/shared/store/selectors/node.selectors'
import {
  selectReusableUiTemplateResourceId,
  selectTemplateConfig,
  selectedAiUserTaskTemplateConfig,
} from '@flowx/designer/shared/store/selectors/template-config.selectors'
import {
  EventHandler,
  EventHandlerActionType,
  EventHandlerTriggerEvent,
} from '@flowx/designer/shared/types/event-handler.types'
import { TemplateConfigInterface } from '@flowx/shared/interfaces'

import { selectSetAiUserTaskRootTemplateConfig } from './ai-template-config.selectors'

import { SaveDraftPayload } from '../agents/designer/api/designer.api'
import { BulkUpdatePayload, DesignerService } from '../agents/designer/services/designer.service'
import { ConversationApiService } from '../common/api/conversation-api.service'
import { ContextDataKeys } from '../conversation/ai-conversation.model'
import { ConversationActions } from '../conversation/conversation.actions'
import { ConversationSelectors } from '../conversation/conversation.selectors'
import { AiDesignerContext, DataModelDraft, DocumentContextReference } from '../types/ai.types'

@Injectable({
  providedIn: 'root',
})
export class AiDesignerService {
  #store = inject(Store)
  #actions$ = inject(Actions)
  #templateConfigApiService = inject(TemplateConfigApiService)
  #reusableTemplatesApi = inject(ReusableTemplatesApiService)
  #nodeApi = inject(NodeApiService)
  #conversationApiService = inject(ConversationApiService)
  #eventHandlerApi = inject(EventHandlerApiService)
  #nodes = this.#store.selectSignal(nodesSelector)

  generateFromDocument(documentContextReference: DocumentContextReference): Observable<any> {
    return of(documentContextReference)
  }

  keepTemplateConfigDraft(): Observable<any> {
    const payload = this.#getSaveTemplateConfigDraftPayload()
    // Must use the same signal as #saveTemplateConfigDraft so the reload targets the
    // resource we just saved to. A missing node id is not reliable here: a process-node
    // target with a failed node lookup also has none, which would reload the process
    // templates (rt/process/...) while in the reusable-ui-template section and 403.
    const reusableTemplateResourceId = this.#store.selectSignal(
      selectReusableUiTemplateResourceId
    )()
    const isReusableTemplateTarget = !!reusableTemplateResourceId
    return this.#saveTemplateConfigDraft(payload).pipe(
      // Persist data model with template config identifiers to thread context
      switchMap((templateConfig) =>
        this.#updateAndPersistDataModelToContext(templateConfig).pipe(map(() => templateConfig))
      ),
      tap(() => {
        if (isReusableTemplateTarget) {
          this.#store.dispatch(
            TemplateConfigActions.loadReusableTemplate({
              id: reusableTemplateResourceId,
            })
          )
          return
        }
        const node = this.#nodes()?.find((n) => n.id === +payload.target?.node?.id)
        const userTaskNavigationAreaId =
          this.#store.selectSignal(ConversationSelectors.userTaskNavigationAreaId)() ||
          node?.navigationAreaId
        this.#store.dispatch(
          TemplateConfigActions.loadUserTaskTemplateConfigs({
            navigationAreaIds: [userTaskNavigationAreaId],
          })
        )
      }),
      switchMap(() =>
        this.#actions$.pipe(
          ofType(
            isReusableTemplateTarget
              ? TemplateConfigActions.loadReusableTemplateSuccess
              : TemplateConfigActions.loadUserTaskTemplateConfigsSuccess
          ),
          take(1)
        )
      )
    )
  }

  #ensureSaveDataActionExists(payload: SaveDraftPayload): Observable<any> {
    // Reusable UI Templates have no host node; submit-button actions are bound at the User Task level.
    if (!payload.target?.node?.id) {
      return of(null)
    }
    const node = this.#nodes()?.find((n) => n.id === parseInt(payload.target?.node?.id, 10))
    const nodeActions = this.#store.selectSignal(selectedNodeActions)()

    const submitButtons = payload.aiContext.actionMappings.filter((b) => b.button_type === 'submit')

    const saveDataActions = (nodeActions ?? []).filter(
      (a) => !!a.manual && !a.repeatable && a.handlerService === ActionHandler.SAVE_DATA
    )

    // If there is at least one submit button and no corresponding SaveData action, create one
    if (submitButtons.length > 0 && saveDataActions.length === 0) {
      const action = createAction({ name: 'saveData' }, node, null, [])
      return this.#nodeApi.createAction(node.id, action).pipe(
        tap((action) =>
          this.#store.dispatch(
            createNodeActionSuccess({
              nodeId: node.id,
              action: action.resourceContent,
            })
          )
        )
      )
    }

    return of(null)
  }

  #mapTemplateConfigComponentsToAiLinkings(
    templateConfig: TemplateConfigInterface,
    draftTemplateConfig: TemplateConfigInterface,
    uiActionMappings: AiDesignerContext['actionMappings']
  ): AiDesignerContext['actionMappings'] {
    const persistedRoot = Array.isArray(templateConfig) ? templateConfig[0] : templateConfig

    if (!persistedRoot) {
      return
    }

    const keyToId = DesignerService.buildKeyToIdMap(draftTemplateConfig, persistedRoot)

    const buttonTemplateIdMappings = DesignerService.linkUiActionMappings(uiActionMappings, keyToId)

    return buttonTemplateIdMappings
  }

  #getSaveTemplateConfigDraftPayload(): SaveDraftPayload {
    const aiGeneratedUiOutcome = this.#store.selectSignal(
      ConversationSelectors.aiGeneratedUiOutcome
    )()
    const target = this.#store.selectSignal(ConversationSelectors.activeAgentTarget)()
    const userTaskRootTemplateConfig = this.#store.selectSignal(
      selectSetAiUserTaskRootTemplateConfig
    )()
    const draftTemplateConfig = this.#store.selectSignal(ConversationSelectors.aiGeneratedUi)()
    const userTaskNavigationAreaId = this.#store.selectSignal(
      ConversationSelectors.userTaskNavigationAreaId
    )()
    const defaultUserTaskNavigationAreaId = this.#store.selectSignal(
      selectedAiUserTaskTemplateConfig
    )()?.navigationAreaId
    const activeAgentTarget = this.#store.selectSignal(ConversationSelectors.activeAgentTarget)()

    return {
      target,
      navAreaId: userTaskNavigationAreaId || defaultUserTaskNavigationAreaId,
      draftConfig: {
        ...draftTemplateConfig,
        uiTemplateParentId:
          parseInt(activeAgentTarget?.node?.templateConfigId, 10) || userTaskRootTemplateConfig?.id,
      },
      mergedConfig: DesignerService.mergeDraftTemplateConfig(
        draftTemplateConfig,
        userTaskRootTemplateConfig,
        parseInt(activeAgentTarget?.node?.templateConfigId, 10)
      ),
      aiContext: {
        answer: JSON.stringify(aiGeneratedUiOutcome),
        actionMappings: (aiGeneratedUiOutcome as any)?.link_buttons || [],
      } as AiDesignerContext,
    }
  }

  #saveTemplateConfigDraft(payload: SaveDraftPayload): Observable<TemplateConfigInterface[]> {
    const { navAreaId, draftConfig, mergedConfig, aiContext } = payload

    // Only persist to the reusable-template API when the user is actually in the
    // reusable-ui-template section (route param present). A missing node id alone is
    // not enough: a process-node target whose node lookup failed also has no node id,
    // and routing that to the reusable API produces a malformed, resource-id-less URL.
    const reusableTemplateResourceId = this.#store.selectSignal(
      selectReusableUiTemplateResourceId
    )()

    if (reusableTemplateResourceId) {
      return this.#saveReusableTemplateDraft(draftConfig)
    }

    const templateConfig = this.#prepareTemplateConfigForBulkUpdate(
      draftConfig,
      mergedConfig,
      aiContext
    )

    if (!templateConfig) {
      return of([])
    }

    return this.#ensureSaveDataActionExists(payload).pipe(
      switchMap(() =>
        this.#templateConfigApiService
          .bulkUpdateTemplateConfigs(navAreaId, { added: templateConfig })
          .pipe(
            concatMap((response) =>
              this.#ensureSaveDataActionExists(payload).pipe(map(() => response.added))
            ),
            tap((templateConfig) => {
              const mappings = this.#createButtonMappings(
                templateConfig,
                draftConfig,
                aiContext.actionMappings
              )

              if (!mappings) {
                return
              }

              const nodeActions = this.#store.selectSignal(selectedNodeActions)()
              this.#createUiActionsForButtons(
                mappings.buttonFormMappings,
                mappings.buttonTemplateIdMappings,
                nodeActions
              )
            })
          )
      )
    )
  }

  #saveReusableTemplateDraft(
    draftConfig: TemplateConfigInterface
  ): Observable<TemplateConfigInterface[]> {
    const resourceId = this.#store.selectSignal(selectReusableUiTemplateResourceId)()
    const reusableTemplateRootId = this.#store.selectSignal(selectTemplateConfig)()?.id
    const containerId = this.#store.selectSignal(
      ConversationSelectors.selectedUiTemplateContainerId
    )()

    return this.#reusableTemplatesApi
      .createNodeTemplate(resourceId, {
        ...this.#stripDraftTemplateConfigIds(draftConfig),
        uiTemplateParentId: containerId || reusableTemplateRootId,
      })
      .pipe(map((response) => [response.resourceContent.template]))
  }

  #stripDraftTemplateConfigIds(config: TemplateConfigInterface): Partial<TemplateConfigInterface> {
    const { id, ...rest } = config
    return {
      ...rest,
      templateConfig: config.templateConfig?.map((child) =>
        this.#stripDraftTemplateConfigIds(child)
      ),
    } as Partial<TemplateConfigInterface>
  }

  #updateAndPersistDataModelToContext(templateConfig: TemplateConfigInterface[]): Observable<any> {
    const dataModelChanges = this.#store.selectSignal(
      ConversationSelectors.aiGeneratedUiDataModelChanges
    )()

    if (!dataModelChanges) {
      return of(null)
    }

    // Augment data model items with template config identifiers
    const augmentedDataModel = DesignerService.updateDataModelWithTemplateConfigIdentifiers(
      dataModelChanges,
      templateConfig
    )

    // Persist augmented data model to thread context
    return this.#patchThreadContextWithDataModel(augmentedDataModel)
  }

  #patchThreadContextWithDataModel(dataModel: DataModelDraft): Observable<any> {
    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    const threadId = this.#store.selectSignal(ConversationSelectors.activeAgentThreadId)()
    const messageWithAgentAction = this.#store.selectSignal(
      ConversationSelectors.latestAgentMessageWithAgentAction
    )()

    if (!conversationId || !threadId || !messageWithAgentAction?.id) {
      console.warn(
        '[AiDesignerService] Cannot patch thread context: missing conversationId, threadId, or messageId'
      )
      return EMPTY
    }

    const generatedDataModelItem = {
      key: ContextDataKeys.GeneratedDataModel,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify(dataModel),
    }

    const context = {
      items: _uniqBy('key', [
        generatedDataModelItem,
        ...(messageWithAgentAction.contextData?.items ?? []),
      ]),
    }

    return this.#conversationApiService
      .patchMessageContext(conversationId, threadId, messageWithAgentAction.id, { context })
      .pipe(
        tap(() => {
          this.#store.dispatch(
            ConversationActions.patchMessageContextItemsApiResponse({
              threadId,
              messageId: messageWithAgentAction.id,
              context,
            })
          )
        })
      )
  }

  #prepareTemplateConfigForBulkUpdate(
    draftConfig: TemplateConfigInterface,
    mergedConfig: TemplateConfigInterface,
    aiContext: AiDesignerContext
  ): BulkUpdatePayload[] | null {
    try {
      return DesignerService.toBulkUpdatePayload({
        draftConfig: [draftConfig],
        mergedConfig,
        aiContext,
      })
    } catch (e) {
      console.error('Error transforming template to AI', e)
      return null
    }
  }

  #createButtonMappings(
    templateConfig: TemplateConfigInterface | TemplateConfigInterface[],
    draftConfig: TemplateConfigInterface,
    actionMappings: AiDesignerContext['actionMappings']
  ): {
    buttonTemplateIdMappings: AiDesignerContext['actionMappings']
    buttonFormMappings: Record<
      number,
      { buttonFlowxUuid: string; forms: { id: number; flowxUuid: string }[] }
    >
  } | null {
    const rootTemplate = Array.isArray(templateConfig) ? templateConfig[0] : templateConfig
    const buttonTemplateIdMappings = this.#mapTemplateConfigComponentsToAiLinkings(
      rootTemplate,
      draftConfig,
      actionMappings
    )

    const buttonFormMappings = DesignerService.mapTemplateConfigComponentsToAiLinkingElements(
      rootTemplate,
      buttonTemplateIdMappings
    )

    if (!buttonFormMappings || !buttonTemplateIdMappings) {
      return null
    }

    return { buttonTemplateIdMappings, buttonFormMappings }
  }

  #createSubmitButtonUiAction(buttonId: number, saveAction: any, formIds: string[]): EventHandler {
    const uiAction: EventHandler = {
      id: null,
      templateConfigId: buttonId,
      type: EventHandlerActionType.ACTION,
      name: saveAction?.name ?? 'saveData',
      triggerEvent: EventHandlerTriggerEvent.CLICK,
      params: {
        analytics: '',
        blocksUi: false,
        customKeys: [],
        excludedKeys: [],
        formsToSubmit: formIds,
      },
    }

    if (saveAction) {
      uiAction.actionFlowxUuid = saveAction.flowxUuid
      uiAction.customBody = ''
    }

    return uiAction
  }

  #createCancelButtonUiAction(buttonId: number): EventHandler {
    return {
      id: null,
      templateConfigId: buttonId,
      type: EventHandlerActionType.DISMISS,
      name: `cancel_${Date.now()}`,
      triggerEvent: EventHandlerTriggerEvent.CLICK,
      params: {
        analytics: '',
      },
    }
  }

  #createUiActionsForButtons(
    buttonFormMappings: Record<
      number,
      { buttonFlowxUuid: string; forms: { id: number; flowxUuid: string }[] }
    >,
    buttonTemplateIdMappings: AiDesignerContext['actionMappings'],
    nodeActions: any[]
  ): void {
    const nodeSaveDataActions = (nodeActions ?? []).filter(
      (a) => !!a.manual && !a.repeatable && a.handlerService === ActionHandler.SAVE_DATA
    )
    const saveAction = nodeSaveDataActions[0]

    Object.entries(buttonFormMappings).forEach(([buttonIdStr, value]) => {
      const buttonId = +buttonIdStr
      const buttonLink = buttonTemplateIdMappings.find((b) => b.templateConfigId === buttonId)

      if (!buttonLink) {
        return
      }

      const isSubmit = buttonLink.button_type === 'submit'
      const isCancel = buttonLink.button_type === 'cancel'

      if (!isSubmit && !isCancel) {
        return
      }

      const uiActionValue = isSubmit
        ? this.#createSubmitButtonUiAction(
            buttonId,
            saveAction,
            value.forms.map((f) => f.flowxUuid)
          )
        : this.#createCancelButtonUiAction(buttonId)

      this.#eventHandlerApi.addEventHandler(buttonId, uiActionValue, undefined).subscribe()
    })
  }
}
